<template>
  <div>
    <scorecards-empty
      v-if="!scorecardGroupsData?.data.length && !scorecardsLoading"
      is-groups
      @new-template="emit('newTemplate')"
    />
    <loader v-else-if="scorecardsLoading" :is-loading="scorecardsLoading" />

    <template v-else>
      <scorecard-group-list
        v-if="activeView === VIEW_TYPE_LIST"
        v-model:selected-ids="selectedIds"
        :data="scorecardGroupsData?.data || []"
        :loading="scorecardsLoading"
        :select="enableSelection"
      />
      <scorecard-groups-tiles
        v-else
        v-model:selected-ids="selectedIds"
        :data="scorecardGroupsData?.data || []"
        :select="enableSelection"
      />
      <lf-pagination
        v-if="scorecardGroupsData?.meta"
        :metadata="scorecardGroupsData.meta"
        show-per-page
        @change-page="changePage"
        @change-items-per-page="changeItemsPerPage"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";

import debounce from "lodash/debounce";

import { AnalyticsEvent } from "@/enums/analytics";
import { SortOption } from "@/enums/scorecards";
import { SCORECARDS_STATUSES, VIEW_TYPE_LIST } from "@/helpers/constants";
import {
  ScorecardsIsViewKey,
  ScorecardsIsWorkflowBuilder,
} from "@/helpers/symbols";
import { useAuth } from "@/hooks/auth";
import { useDealsBase } from "@/hooks/deals";
import { useScorecardsBase } from "@/hooks/scorecards";
import { useActiveStage, useActiveWorkflowTemplate } from "@/hooks/workflow";
import { useAnalyticsStore } from "@/stores/analytics";

import ScorecardGroupList from "@/components/scorecards/ScorecardGroupList.vue";
import ScorecardGroupsTiles from "@/components/scorecards/ScorecardGroupsTiles.vue";
import ScorecardsEmpty from "@/components/scorecards/ScorecardsEmpty.vue";
import LfPagination from "@/components/ui/table/LfPagination.vue";

import type {
  IRunScorecardGroupsPayload,
  IScorecardsViewParams,
} from "@/models/scorecards";
import type { PropType } from "vue";

const emit = defineEmits<{
  (eventName: "updateParams", newParams: IScorecardsViewParams): void;
  (eventName: "newTemplate"): void;
}>();

const props = defineProps({
  activeView: {
    type: String,
    required: true,
  },
  params: {
    type: Object as PropType<IScorecardsViewParams>,
    required: true,
  },
  enableSelection: {
    type: Boolean,
    default: false,
  },
  showActionMenu: {
    type: Boolean,
    default: true,
  },
});

const { dispatch, commit, getters } = useStore();
const { scorecardGroupsData, runScorecardIds } = useScorecardsBase();
const { trackEvent } = useAnalyticsStore();
const { activeStage, isManagedByLendflow } = useActiveStage();
const { activeTemplate } = useActiveWorkflowTemplate();
const { isLendflowUser } = useAuth();
const { activeDeal } = useDealsBase();

const scorecardsLoading = ref(false);
const selectedIds = ref<string[]>([]);
const isWorkflowBuilder = inject(ScorecardsIsWorkflowBuilder);
const pageIsView = inject(ScorecardsIsViewKey, true);

const isWorkflowLive = !pageIsView && !isWorkflowBuilder;

const groupHasBeenRun = computed<boolean>(
  () => getters["scorecards/groupHasBeenRun"]
);

const updateParams = (newParams: Partial<IScorecardsViewParams>) => {
  const updatedParams = { ...props.params, ...newParams };
  emit("updateParams", updatedParams);
};

const changePage = (page: number) => {
  updateParams({ page });
};

const changeItemsPerPage = (per_page: number) => {
  updateParams({ per_page, page: 1 });
};

const getScorecardGroups = async () => {
  scorecardsLoading.value = true;
  const params = {
    ...props.params,
  };

  if (isWorkflowBuilder && isLendflowUser) {
    params.client_id = activeTemplate.value.client?.id || "";
    if (isManagedByLendflow.value) {
      params.include_lendflow = 1;
    }
  }

  if (isWorkflowBuilder) {
    params.statuses = [SCORECARDS_STATUSES.FINISHED];
  }

  if (isWorkflowLive) {
    params.workflow_stage_id = activeStage.value?.id;
  }

  if (activeStage.value) {
    params.sort_by = SortOption.LATEST_RUN_FIRST;
  }

  await dispatch("scorecards/getScorecardGroups", params);
  scorecardsLoading.value = false;
};

const handleRun = debounce(async (ids: string[]) => {
  if (!activeStage.value) {
    return;
  }

  const payload: {
    appId: string;
    data: IRunScorecardGroupsPayload;
  } = {
    appId: activeDeal.value.id,
    data: { stage_id: activeStage.value.id },
  };

  payload.data = {
    ["score_card_group_ids"]: ids,
    stage_id: activeStage.value.id,
  };
  await dispatch("scorecards/runScorecardGroups", payload);

  trackEvent(AnalyticsEvent.ScorecardGroupRunTriggered, {
    scorecard_group_ids: ids,
    scorecard_group_count: ids.length,
  });
}, 1500);

watch(
  () => props.params.search,
  () => {
    updateParams({ page: 1 });
  }
);

watch(groupHasBeenRun, async (newValue, oldValue) => {
  if (newValue && !oldValue) {
    await getScorecardGroups();
    commit("scorecards/setGroupHasBeenRun", false);
  }
});

watch(runScorecardIds, (newValue, oldValue) => {
  if (newValue.length && newValue.length > oldValue.length) {
    handleRun(newValue);
  }
});

watch(() => props.params, getScorecardGroups);

onMounted(() => {
  getScorecardGroups();

  if (isWorkflowBuilder) {
    selectedIds.value = activeStage.value?.groups || [];
  }
});

onBeforeUnmount(() => {
  commit("scorecards/clear");
});

defineExpose({
  selectedIds,
  scorecardsLoading,
});
</script>
